<template>
  <div class="is-flex">
    <div class="field">
      <label class="label">
        <span>{{ item.name }}</span>
        <span v-if="canSaveLocal"
            class="is-pulled-right">
          <label class="has-text-weight-normal mr-1">{{ $t('save') }}</label>
          <input v-model="saveLocal"
                type="checkbox" />
        </span>
      </label>
      <div v-if="canEdit"
          class="field has-addons has-addons-right p-0">
        <p class="control is-expanded">
          <div class="select is-fullwidth"
                :class="{'is-danger': !isValid}">
            <select v-model="selectedAddressRefKey"
                    @change="onSelectAddressOption">
              <option :value="null">
                {{ $t('notSelected') }}
              </option>
              <option v-for="(address, index) in addressOptions"
                      :key="index"
                      :value="address.refKey">
                {{ toString(address) }}
              </option>
            </select>
          </div>
          <p v-if="(addressVerificationStatus === AddressStatus.UNVERIFIED || addressVerificationStatus === AddressStatus.ADD_SUBPREMISE) && showVerificationStatus"
             class="help is-marginless">
            {{ $t('addressState.unverifiedHelpText') }}
          </p>
          <p v-if="!isValid" class="help is-danger is-marginless">
            {{ $t('invalidField', {field: item.name}) }}
          </p>
        </p>
        <p class="control flex-button">
          <button class="button"
                  :class="{'is-danger': !isValid}"
                  :disabled="isEditDisabled"
                  @click="onAddressEdit">
          <span class="icon">
            <i class="fa fa-edit" />
          </span>
          </button>
        </p>
        <p class="control">
          <button class="button"
                  :class="{'is-danger': !isValid}"
                  :disabled="!(this.item.editable ?? false)"
                  @click="onAddressAdd">
            <span class="icon">
              <i class="fa fa-plus-circle"/>
            </span>
          </button>
        </p>
      </div>
      <div class="address">
        <article v-if="selectedAddress"
                 class="is-flex is-justify-content-space-between">
          <p v-if="selectedAddress"
             v-html="toParagraphString(selectedAddress)" />
          <div class="mt-2">
            <verification-status v-if="showVerificationStatus"
                                 :status="addressVerificationStatus" />
          </div>
        </article>
      </div>
      <address-verification-modal v-if="editAddressModalOpen"
                                  :address="isNewAddress ? null : selectedAddress"
                                  :initial-address-status="isNewAddress ? null : addressVerificationStatus"
                                  :is-required="item.required ?? false"
                                  :session-token="sessionToken"
                                  :title="item.name"
                                  @close="editAddressModalOpen = false"
                                  @save="onCustomAddress" />
    </div>
  </div>
</template>

<script>
import AddressVerificationModal from './address/AddressVerificationModal.vue'
import getSaveLocalState from '@/components/library/orders/cells/getSaveLocalState'
import VerificationStatus from '@/components/common/VerificationStatus.vue'
import { AddressStatus } from '@/interfaces/admin/places'
import { v4 as uuid } from 'uuid'
import { validate } from '@/controllers/global/places'

export default {
  name: 'OrderCellAddress',
  components: {
    AddressVerificationModal,
    VerificationStatus
  },
  props: {
    /**
     * There are three states today. This is used for visibility of the dropdown field,
     * edit button, and add button.
     *  - Purchase Orders > canEdit = true
     *  - Orders > canEdit = false
     *  - Orders + isOrderApprover + statusCode === 'OPEN' > canEdit = true
     *
     * The last state is a rare edge-case. When an order fails, the order would be open.
     * Order Approvers/Managers theoretically can edit order fields today. (Orders are
     * a messy design. :c)
     */
    canEdit: Boolean,
    canSaveLocal: Boolean,
    data: { // Overloaded value of field, including locally-stored address
      type: Object,
      default: () => {}
    },
    item: { // Field metadata
      type: Object,
      required: true
    }
  },
  computed: {
    isEditDisabled () {
      return !this.item.editable || !this.addressOptions.length
    },
    isValid () {
      if (this.item.required) return !!this.selectedAddress
      else return true
    },
    sessionToken() {
      return this.$store.state.order.addressValidationSessionToken
    },
    showVerificationStatus() {
      return this.canEdit &&
        this.item.editable &&
        this.addressVerificationStatus !== null && // error state
        !!this.selectedAddress
    }
  },
  data () {
    return {
      AddressStatus, // Expose enum to template
      addressVerificationStatus: 'UNVERIFIED', // Local verification state
      editAddressModalOpen: false,
      isNewAddress: false, // Controls data passed to modal
      selectedAddress: null, // Address-object data
      selectedAddressRefKey: null // Value for dropdown menu
    }
  },
  watch: {
    saveLocal () {
      this.change()
    }
  },
  methods: {
    change () {
      this.$emit('change', {
        field: this.item,
        value: this.selectedAddress,
        updateLocal: this.saveLocal && this.isValid
      })
    },
    onAddressAdd () {
      this.isNewAddress = true
      this.editAddressModalOpen = true
    },
    onAddressEdit () {
      this.isNewAddress = false
      this.editAddressModalOpen = true
    },
    async validateAddress() {
      if (!this.selectedAddress || !this.showVerificationStatus) return

      this.addressVerificationStatus = null
      try {
        const response = await validate(this.selectedAddress, this.sessionToken)
        this.addressVerificationStatus = response.status
      } catch (error) {
        /**
         * Short of an API outage, unsupported addresses would be the most common error.
         * And this will default to UNVERIFIED.
         */
        console.error('Failed to validate address:', error)
        this.addressVerificationStatus = AddressStatus.UNVERIFIED
      }
    },
    onCustomAddress(payload) {
      this.isNewAddress = false
      this.selectedAddress = payload.address
      this.selectedAddressRefKey = null
      this.addressVerificationStatus = payload.addressStatus
      this.change()
    },
    // Update displayed address below input field
    async onSelectAddressOption () {
      this.selectedAddress = this.addressOptions
        .find(item => item.refKey === this.selectedAddressRefKey) ?? null
      this.change()
      await this.validateAddress()
    },
    toParagraphString(address) {
      let formatedAddress = `${address.line1}`
      if (address.line2) {
        formatedAddress += `<br>${address.line2}`
      }
      if (address.line3) {
        formatedAddress += `<br>${address.line3}`
      }
      if (address.city) {
        formatedAddress += `<br>${address.city},`
      }
      formatedAddress += ` ${address.state} ${address.zipCode}`
      return formatedAddress
    },
    toString(address) {
      if (!address) {
        return ''
      }
      return  [address.line1, address.line2, address.line3, address.city + ',', address.state, address.zipCode].filter(Boolean).join(" ")
    }
  },
  async mounted () {
    let initialAddressId = null

    // Locally-stored data takes higher priority than default
    if (!!this.data) {
      this.selectedAddress = this.data
      initialAddressId = this.data?.id ?? null
    } else if (!!this.item.defaultValue) {
      this.selectedAddress = this.item.defaultValue
      initialAddressId = this.item.defaultValue.id ?? null
    }

    if (!!initialAddressId) {
      this.selectedAddressRefKey = this.addressOptions.find(
        (item) => item.id === initialAddressId
      ).refKey
    }

    await this.validateAddress()
  },
  setup (props) {
    const { saveLocal } = getSaveLocalState(props)
    /**
     * ERP addresses are externally-sourced and is missing ID.
     * Ref keys are the unique id when referencing most address options.
     */
    const addressOptions = props.item.options?.map(item => {
        return {
          ...item,
          refKey: uuid()
        }
      }) ?? []

    return {
      addressOptions,
      saveLocal
    }
  }
}
</script>

<style scoped lang="scss">
.field {
  padding: .5rem 1rem;
  width: 100%;
}
// Prevent loss of button size in different window widths
.flex-button {
  flex-shrink: 0;
}
.address {
  margin-top: .5rem;
}
</style>
